import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Search } from "lucide-react";
import { fetchHelp } from "../lib/api";

const Block = ({ b }) => {
  if (b.kind === "text")
    return <p className="text-sm leading-[1.9] text-zinc-300">{b.text}</p>;

  if (b.kind === "note")
    return (
      <p className="border-l-2 border-[var(--line-hi)] pl-4 text-sm leading-[1.9] text-zinc-500">
        {b.text}
      </p>
    );

  if (b.kind === "steps")
    return (
      <ol className="space-y-3">
        {b.steps.map((s, i) => (
          <li key={s} className="grid grid-cols-[32px_1fr] gap-3">
            <span className="font-data text-[10px] leading-6 text-zinc-700">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="text-sm leading-[1.9] text-zinc-300">{s}</span>
          </li>
        ))}
      </ol>
    );

  if (b.kind === "table")
    return (
      <dl>
        {b.rows.map(([k, v]) => (
          <div
            key={k}
            className="grid gap-2 border-b border-[var(--line)] py-3 sm:grid-cols-[200px_1fr] sm:gap-6"
          >
            <dt className="font-data text-[11px] uppercase tracking-[0.12em] text-zinc-500">
              {k}
            </dt>
            <dd className="text-sm leading-[1.9] text-zinc-300">{v}</dd>
          </div>
        ))}
      </dl>
    );

  return null;
};

export default function Help() {
  const [d, setD] = useState(null);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    fetchHelp()
      .then((data) => {
        setD(data);
        setActive(data.sections[0]?.id ?? null);
      })
      .catch(() => {});
  }, []);

  const sections = d?.sections || [];
  const hits = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return sections;
    return sections.filter((s) =>
      JSON.stringify(s).toLowerCase().includes(needle)
    );
  }, [sections, q]);

  const current = sections.find((s) => s.id === active) || hits[0];

  const replay = () => window.dispatchEvent(new Event("covalence:replay-tour"));

  return (
    <div
      data-testid="help-page"
      className="grain flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--void)] lg:flex-row"
    >
      <nav className="thin-scroll w-full shrink-0 overflow-y-auto border-b border-[var(--line)] px-5 py-7 lg:w-[16rem] lg:border-b-0 lg:border-r">
        <button
          onClick={() => nav(-1)}
          data-testid="help-back-link"
          className="label flex items-center gap-1.5 hover:text-zinc-200"
        >
          <ArrowLeft size={11} strokeWidth={1.7} /> back
        </button>

        <div className="mt-6 flex items-center gap-2 border-b border-[var(--line)] pb-2">
          <Search size={12} strokeWidth={1.7} className="text-zinc-600" />
          <input
            data-testid="help-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="search the guide"
            className="w-full bg-transparent font-data text-[11px] text-zinc-100 outline-none placeholder:text-zinc-700"
          />
        </div>

        <button
          data-testid="help-replay-tour-btn"
          onClick={replay}
          className="label mt-5 flex w-full items-center gap-2 border border-[var(--line-hi)] px-3 py-2.5 transition-colors hover:text-zinc-100"
        >
          <Play size={10} strokeWidth={2} /> replay walkthrough
        </button>

        {(d?.groups || []).map((g) => {
          const rows = hits.filter((s) => s.audience === g.id);
          if (rows.length === 0) return null;
          return (
            <div key={g.id} className="mt-7">
              <p className="label px-1 !text-[9px] !text-zinc-700">
                {g.title}
                {g.operator_only && " · operator only"}
              </p>
              <ul className="mt-2">
                {rows.map((s) => (
                  <li key={s.id}>
                    <button
                      data-testid={`help-nav-${s.id}`}
                      onClick={() => setActive(s.id)}
                      className={`w-full px-1 py-2 text-left font-data text-[11px] leading-snug transition-colors ${
                        current?.id === s.id
                          ? "text-zinc-100"
                          : "text-zinc-500 hover:text-zinc-200"
                      }`}
                    >
                      {s.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      <div className="thin-scroll min-w-0 flex-1 overflow-y-auto px-6 py-10 sm:px-12">
        {current ? (
          <article
            data-testid={`help-section-${current.id}`}
            className="mx-auto max-w-2xl space-y-7"
          >
            <header>
              {current.operator_only && (
                <span
                  data-testid="help-operator-badge"
                  className="border border-[var(--line-hi)] px-2 py-1 font-data text-[9px] uppercase tracking-[0.18em] text-zinc-300"
                >
                  operator only · not visible to subjects
                </span>
              )}
              <h1 className="mt-4 font-display text-4xl leading-none tracking-tight sm:text-5xl">
                {current.title}
              </h1>
              <p className="mt-3 text-base text-zinc-500">{current.summary}</p>
            </header>
            {current.blocks.map((b, i) => (
              <Block key={i} b={b} />
            ))}
            <p className="border-t border-[var(--line)] pt-5 font-data text-[10px] text-zinc-700">
              Guide v{d.version} · {d.audience === "operator" ? "operator view" : "subject view"}
            </p>
          </article>
        ) : (
          <p className="label">Loading the guide…</p>
        )}
      </div>
    </div>
  );
}
